/**
 * The Outliner's view model: which rows are visible, how each is labeled, and
 * how keys move through them. Pure functions of a world snapshot, so the tree's
 * behavior is unit-tested without a DOM.
 *
 * Row labels come from the component schemas' `display` hints (never from
 * component-specific code, rule 4): the lowest-order component with an
 * `outlinerType` names the row, like Unreal's actor type column.
 */
import type { EntityId, EntityRecord } from "../../world/entity";
import type { ComponentRegistry } from "../../world/schema/ComponentRegistry";
import type { WorldSnapshot } from "../../world/World";

export interface OutlinerRow {
  readonly id: EntityId;
  readonly name: string;
  readonly depth: number;
  readonly hasChildren: boolean;
  readonly expanded: boolean;
  /** Type column text, or null for groups and plain entities. */
  readonly typeLabel: string | null;
  /** An icon name: a schema `display.icon`, "folder" for groups, or "entity". */
  readonly icon: string;
  /** True when the row itself matches the search (ancestors shown for context are false). */
  readonly matches: boolean;
  readonly parent: EntityId | null;
  /**
   * "hidden": the entity's own eye is off. "inherited": it's on, but an
   * ancestor is hidden, so it doesn't draw either (Unreal dims that eye).
   */
  readonly visibility: "visible" | "hidden" | "inherited";
}

/**
 * The rows to draw, depth-first. With a filter, every entity whose name
 * matches is shown with its ancestors, all open, and the expansion set is
 * ignored (as in Unreal's Outliner search).
 */
export function visibleRows(
  snapshot: WorldSnapshot,
  components: ComponentRegistry,
  expanded: ReadonlySet<EntityId>,
  filter = "",
): OutlinerRow[] {
  const query = filter.trim().toLowerCase();
  const matching = query === "" ? null : matchingBranches(snapshot, query);
  const rows: OutlinerRow[] = [];

  const visit = (id: EntityId, depth: number, ancestorHidden: boolean) => {
    const entity = snapshot.entities.get(id);
    if (!entity || (matching && !matching.has(id))) return;

    const open = matching ? true : expanded.has(id);
    const visibility = !entity.visible ? "hidden" : ancestorHidden ? "inherited" : "visible";
    rows.push({ ...rowFor(entity, depth, open, components, query), visibility });
    if (open) {
      for (const child of entity.children)
        visit(child, depth + 1, ancestorHidden || !entity.visible);
    }
  };

  for (const root of snapshot.roots) visit(root, 0, false);
  return rows;
}

/** Parents of an entity, nearest first. */
export function ancestorsOf(snapshot: WorldSnapshot, id: EntityId): EntityId[] {
  const chain: EntityId[] = [];
  for (let parent = snapshot.entities.get(id)?.parent ?? null; parent !== null;) {
    chain.push(parent);
    parent = snapshot.entities.get(parent)?.parent ?? null;
  }
  return chain;
}

/**
 * A newly opened level shows whole: every entity with children starts open, as
 * in Unreal's Outliner, so the scene reads at a glance.
 */
export function initialExpansion(snapshot: WorldSnapshot): Set<EntityId> {
  return new Set(
    [...snapshot.entities.values()]
      .filter((record) => record.children.length > 0)
      .map((record) => record.id),
  );
}

/**
 * Opens entities that arrived with children since `previous` (a UI Canvas with
 * its Text, a pasted or duplicated group), so a new hierarchy shows what's in
 * it, as Unreal's Outliner does. Returns `expanded` itself when nothing opens.
 */
export function openNewParents(
  expanded: ReadonlySet<EntityId>,
  previous: ReadonlyMap<EntityId, EntityRecord>,
  next: ReadonlyMap<EntityId, EntityRecord>,
): ReadonlySet<EntityId> {
  const arrived = [...next.values()].filter(
    (record) => record.children.length > 0 && !previous.has(record.id) && !expanded.has(record.id),
  );
  if (arrived.length === 0) return expanded;
  return new Set([...expanded, ...arrived.map((record) => record.id)]);
}

export type TreeKeyAction =
  | { readonly focus: EntityId }
  | { readonly expand: EntityId }
  | { readonly collapse: EntityId }
  | { readonly select: EntityId }
  | { readonly rename: EntityId };

/** Keyboard behavior of the WAI-ARIA tree pattern (SPEC §11), for the focused row. */
export function treeKeyAction(
  rows: readonly OutlinerRow[],
  focused: EntityId,
  key: string,
): TreeKeyAction | null {
  const index = rows.findIndex((row) => row.id === focused);
  const row = rows[index];
  if (!row) return null;

  const focusAt = (i: number) => {
    const target = rows[i];
    return target ? { focus: target.id } : null;
  };

  switch (key) {
    case "ArrowDown":
      return focusAt(index + 1);
    case "ArrowUp":
      return focusAt(index - 1);
    case "Home":
      return focusAt(0);
    case "End":
      return focusAt(rows.length - 1);
    case "ArrowRight":
      if (!row.hasChildren) return null;
      return row.expanded ? focusAt(index + 1) : { expand: row.id };
    case "ArrowLeft":
      if (row.hasChildren && row.expanded) return { collapse: row.id };
      return row.parent === null ? null : { focus: row.parent };
    case "Enter":
    case " ":
      return { select: row.id };
    // Unreal's Outliner renames with F2.
    case "F2":
      return { rename: row.id };
    default:
      return null;
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function rowFor(
  entity: EntityRecord,
  depth: number,
  expanded: boolean,
  components: ComponentRegistry,
  query: string,
): Omit<OutlinerRow, "visibility"> {
  const display = entityDisplay(entity, components);
  const hasChildren = entity.children.length > 0;

  return {
    id: entity.id,
    name: entity.name,
    depth,
    hasChildren,
    expanded: hasChildren && expanded,
    typeLabel: display.label,
    icon: display.icon,
    matches: query !== "" && entity.name.toLowerCase().includes(query),
    parent: entity.parent,
  };
}

/**
 * How an entity is labeled everywhere (Outliner rows, Inspector header): its
 * primary component's type and icon, else a folder for groups or a plain entity.
 */
export function entityDisplay(
  entity: EntityRecord,
  components: ComponentRegistry,
): { label: string | null; icon: string } {
  const typed = primaryDisplay(entity, components);
  if (typed) return typed;
  return { label: null, icon: entity.children.length > 0 ? "folder" : "entity" };
}

/** The lowest-order component that declares an Outliner type. */
function primaryDisplay(
  entity: EntityRecord,
  components: ComponentRegistry,
): { label: string; icon: string } | null {
  let best: { order: number; label: string; icon: string } | null = null;

  for (const type of entity.components.keys()) {
    const display = components.get(type)?.display;
    if (!display?.outlinerType) continue;
    if (best && best.order <= display.order) continue;
    best = { order: display.order, label: display.outlinerType, icon: display.icon ?? "entity" };
  }
  return best && { label: best.label, icon: best.icon };
}

/** Entities that match the query, plus all their ancestors. */
function matchingBranches(snapshot: WorldSnapshot, query: string): Set<EntityId> {
  const keep = new Set<EntityId>();
  for (const entity of snapshot.entities.values()) {
    if (!entity.name.toLowerCase().includes(query)) continue;
    keep.add(entity.id);
    ancestorsOf(snapshot, entity.id).forEach((id) => keep.add(id));
  }
  return keep;
}
